import type { QueueRecord } from './types';

/**
 * Thin IndexedDB wrapper for the kiosk queue. No library: one store, five
 * operations, and it has to work in an Android WebView of unknown vintage.
 *
 * Blobs are stored directly. Every engine we care about supports Blob values in
 * IndexedDB, and it means a crash mid-event loses nothing.
 */

const DB_NAME = 'incomer-clips';
const DB_VERSION = 1;
const STORE = 'queue';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  const promise: Promise<IDBDatabase> = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('status', 'status');
        store.createIndex('nextAttemptAt', 'nextAttemptAt');
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // A version change from another tab must not leave us holding a stale
      // connection that blocks the upgrade.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  }).catch((err: unknown) => {
    // Let a later call retry rather than caching the failure forever.
    dbPromise = null;
    throw err instanceof Error ? err : new Error(String(err));
  });
  dbPromise = promise;
  return promise;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const req = fn(transaction.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
        transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB tx aborted'));
      }),
  );
}

export function idbPut(record: QueueRecord): Promise<IDBValidKey> {
  return tx('readwrite', (store) => store.put(record));
}

export function idbGet(id: string): Promise<QueueRecord | undefined> {
  return tx('readonly', (store) => store.get(id) as IDBRequest<QueueRecord | undefined>);
}

export function idbDelete(id: string): Promise<undefined> {
  return tx('readwrite', (store) => store.delete(id));
}

export function idbAll(): Promise<QueueRecord[]> {
  return tx('readonly', (store) => store.getAll() as IDBRequest<QueueRecord[]>);
}

export function idbCount(): Promise<number> {
  return tx('readonly', (store) => store.count());
}

export async function idbAvailable(): Promise<boolean> {
  try {
    await openDb();
    return true;
  } catch {
    return false;
  }
}
